import { httpClient } from './http-client';

export type BillCategory =
  | 'CreditCard' | 'Housing' | 'Utility' | 'Telecom'
  | 'Tax' | 'Insurance' | 'Vehicle' | 'Other';

export type BillStatus =
  | 'Upcoming' | 'Due' | 'Overdue' | 'Paid' | 'NoBalance' | 'Skipped';

export type BillPaymentMethod =
  | 'Manual' | 'AutoPay' | 'Chexy' | 'PreAuthorized' | 'Unknown';

export interface BillPayee {
  id: string;
  name: string;
  category: BillCategory;
  liability_account_id?: string | null;
  funding_account_id?: string | null;
  payment_method: BillPaymentMethod;
  owner_label?: string | null;
  attributed_to?: string | null;
  currency: string;
  typical_due_day?: number | null;
  /** Scheduled payment: goes out this many days before the due date (0-28). */
  pay_days_before_due?: number | null;
  /** When on, saved bills are scheduled and verified nightly against the bank. */
  schedule_enabled?: boolean;
  is_active: boolean;
  notes?: string | null;
}

/**
 * Where a scheduled bill stands. scheduled: waiting for the pay date;
 * matched: a posted transaction proved it; needs_review: the bill changed
 * after it was scheduled; not_seen: no payment appeared in the grace period.
 */
export type ScheduleState = 'scheduled' | 'matched' | 'needs_review' | 'not_seen';

export interface Bill extends BillPaymentDetail {
  id: string;
  payee_id: string;
  period: string;
  due_date?: string | null;
  amount_due?: string | null;
  amount_paid?: string | null;
  paid_on?: string | null;
  status: BillStatus;
  /** Status resolved against today by the server; prefer this for display. */
  effective_status: BillStatus;
  amount_outstanding: string;
  source_note?: string | null;
  payee_name: string;
  payee_category: BillCategory;
  payee_owner?: string | null;
  payee_attributed_to?: string | null;
  /** Resolved: the bill's own method if set, else the payee default. */
  payment_method: BillPaymentMethod;
  /** Set only when this bill overrides the payee default. */
  method_override?: BillPaymentMethod | '';
  /** The account a recorded payment came out of. */
  paid_from_account_id?: string | null;
  /** Snapshot taken when the bill was saved under its payee's schedule. */
  scheduled_pay_on?: string | null;
  scheduled_amount?: string | null;
  scheduled_due_date?: string | null;
  schedule_state?: ScheduleState | null;
  schedule_checked_at?: string | null;
  /** Why the verifier flagged it, or what it matched. */
  schedule_note?: string | null;
}

export interface BillPaymentDetail {
  statement_balance?: string | null;
  minimum_due?: string | null;
  /** Cost of settling the bill, kept apart from what was billed. */
  payment_fee?: string | null;
  posted_on?: string | null;
  posted_amount?: string | null;
  carries_balance?: boolean;
  /** unpaid | minimum | partial | full | over */
  coverage?: string;
  revolving?: string;
  /** matched | pending | unposted | none */
  reconciliation?: string;
  marked_paid_at?: string | null;
  marked_paid_manually?: boolean;
}

export interface PayeeSuggestion {
  name: string;
  category: BillCategory;
  occurrences: number;
  median_amount: string;
  typical_day: number;
  last_seen: string;
  funding_account_id?: string | null;
  funding_account_name?: string;
}

export interface BillSummary {
  total_due: string;
  total_paid: string;
  outstanding: string;
  count_paid: number;
  count_due: number;
  count_overdue: number;
}

export interface CategoryTotal {
  category: BillCategory;
  total_due: string;
  total_paid: string;
  outstanding: string;
  count: number;
}

export interface BillsResponse {
  from: string;
  to: string;
  bills: Bill[];
  summary: BillSummary;
  categories: CategoryTotal[];
}

export interface RecordPaymentInput {
  amount?: string;
  paid_on?: string;
  from_account_id?: string;
  note?: string;
  /** What the biller says is owed. Required to tell "in full" from "partial". */
  statement_balance?: string;
  minimum_due?: string;
  /** Fee charged to make the payment (card processing, Chexy). */
  payment_fee?: string;
  /** Rail used for THIS payment. Omit to keep the payee default. */
  payment_method?: BillPaymentMethod;
}

/**
 * What a payee is expected to cost in a period, derived from what it has cost
 * before. `expected_amount` is an estimate and is deliberately not
 * `amount_due` — a prediction and a statement are different kinds of fact.
 */
export interface BillForecast {
  payee_id: string;
  name: string;
  category: BillCategory;
  owner?: string;
  typical_due_day?: number | null;
  due_date?: string | null;
  expected_amount?: string | null;
  last_amount?: string | null;
  last_paid_on?: string | null;
  last_period?: string | null;
  last_from_account_id?: string | null;
  last_from_account_name?: string;
  last_method?: string;
  times_paid: number;
  /** The bill already sitting in this period, when there is one. */
  bill_id?: string | null;
  bill_status?: string;
  amount_due?: string | null;
}

export interface ForecastResponse {
  period: string;
  forecast: BillForecast[];
  /** Billed where known, estimated where not. */
  expected_total: string;
  /** Payees with no bill row for the period yet. */
  not_opened: number;
}

export interface MerchantLine {
  merchant: string;
  amount: string;
  count: number;
}

export interface SpendLine {
  category: string;
  amount: string;
  count: number;
  merchants?: MerchantLine[];
}

export interface BreakdownTxn {
  id: string;
  date: string;
  merchant: string;
  amount: string;
  category?: string;
}

export interface BillBreakdown {
  bill_id: string;
  payee: string;
  amount: string;
  window: { from: string; to: string };
  charged: string;
  count: number;
  categories: SpendLine[];
  uncategorised: number;
  /** False when the payee settles no account held here, with `reason` saying so. */
  explained: boolean;
  reason?: string;
}

export interface BreakdownResponse {
  breakdown: BillBreakdown;
  /** Statement minus charges: interest, fees, and any carried balance. */
  unexplained: string;
  note: string;
  merchant?: string;
  transactions?: BreakdownTxn[];
}

/** One transaction behind a bill. Amounts are positive; `kind` says which way. */
export interface BillTxn {
  id: string;
  date: string;
  description: string;
  merchant?: string;
  amount: string;
  category: string;
  category_id: string | null;
  kind: 'charge' | 'payment' | 'credit';
}

export interface BillCategoryLine {
  category: string;
  category_id: string | null;
  amount: string;
  count: number;
  /** Share of total charges, 0-100. */
  percent: string;
}

/** GET /bills/:id/transactions: every transaction behind one bill. */
export interface BillTransactionsResponse {
  bill_id: string;
  payee_id: string;
  payee: string;
  payee_category: string;
  period: string;
  account: { id: string; name: string; last4?: string | null } | null;
  /** card: charges on the settled card; payment: the matched payment; none: neither. */
  source: 'card' | 'payment' | 'none';
  window: { from: string; to: string };
  statement_amount: string | null;
  transactions: BillTxn[];
  payments_credits: BillTxn[];
  categories: BillCategoryLine[];
  totals: {
    charges: string;
    credits: string;
    payments: string;
    net: string;
    statement_amount: string | null;
    difference: string | null;
  };
  reason?: string;
}

export interface QuickPayInput {
  amount?: string;
  paid_on?: string;
  from_account_id?: string;
  /** What it cost to pay: card processing, Chexy. Never copied from last time. */
  payment_fee?: string;
}

export interface SaveBillInput {
  id?: string;
  payee_id: string;
  /** Any date in the month; the server normalises to the first. */
  period: string;
  due_date?: string;
  amount_due?: string;
  status?: BillStatus;
  statement_balance?: string;
  minimum_due?: string;
  payment_fee?: string;
  source_note?: string;
}

export interface ScheduleOutcome {
  bill_id: string;
  payee_name: string;
  period: string;
  scheduled_pay_on: string;
  scheduled_amount: string;
  previous_state: string;
  action: 'matched' | 'needs_review' | 'not_seen' | 'waiting' | 'error';
  note?: string;
  transaction_id?: string;
  transaction_date?: string;
  matched_side?: 'liability' | 'funding';
  ledger_backed?: boolean;
  entry_posted?: boolean;
  alert?: boolean;
}

export interface ScheduleRunResult {
  as_of: string;
  dry_run: boolean;
  checked: number;
  matched: number;
  needs_review: number;
  not_seen: number;
  waiting: number;
  errors: number;
  outcomes: ScheduleOutcome[];
}

export interface ConfirmPostedInput {
  posted_on?: string;
  posted_amount?: string;
}

class BillsService {
  private static readonly BASE = '/bills';

  static async list(from?: string, to?: string): Promise<BillsResponse> {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return httpClient.get<BillsResponse>(`${BillsService.BASE}${suffix}`);
  }

  static async listPayees(): Promise<BillPayee[]> {
    const res = await httpClient.get<{ payees: BillPayee[] }>(`${BillsService.BASE}/payees`);
    return res.payees ?? [];
  }

  static async savePayee(p: Partial<BillPayee> & { name: string }): Promise<BillPayee> {
    return httpClient.post<BillPayee>(`${BillsService.BASE}/payees`, p);
  }

  /**
   * Record a payment that has already been made at the user's bank.
   *
   * This does NOT initiate a transfer. The server posts a balanced ledger
   * entry and returns `recorded_only: true`.
   */
  static async recordPayment(
    billId: string, input: RecordPaymentInput,
  ): Promise<{ bill: Bill; entry_id: string; recorded_only: boolean }> {
    return httpClient.post(`${BillsService.BASE}/${billId}/record-payment`, input);
  }

  /**
   * Record that the bank actually took the money.
   *
   * Deliberately separate from recordPayment: that captures what the user
   * says they paid, this captures what the account shows. Keeping them apart
   * is what lets a payment that never landed be spotted.
   */
  static async confirmPosted(
    billId: string, input: ConfirmPostedInput,
  ): Promise<{ bill: Bill; recorded_only: boolean }> {
    return httpClient.post(`${BillsService.BASE}/${billId}/confirm-posted`, input);
  }

  /** Recurring outflows that look like untracked bills. Read-only. */
  static async suggestions(): Promise<PayeeSuggestion[]> {
    const r = await httpClient.get<{ suggestions: PayeeSuggestion[] }>(
      `${BillsService.BASE}/suggestions`);
    return r.suggestions ?? [];
  }

  /**
   * Open a period for every active payee, creating the Upcoming rows.
   *
   * Additive and idempotent: a payee that already has a bill for the period is
   * left alone, so anything already entered or paid is never overwritten.
   */
  static async rollForward(period?: string): Promise<{ period: string; created: number; scheduled?: number }> {
    return httpClient.post(`${BillsService.BASE}/roll-forward`, period ? { period } : {});
  }

  /**
   * What one bill's number is made of, by category then merchant.
   *
   * Pass `merchant` to drill the last level: the individual charges behind
   * one counterparty.
   */
  static async breakdown(billId: string, merchant?: string): Promise<BreakdownResponse> {
    const qs = merchant ? `?merchant=${encodeURIComponent(merchant)}` : '';
    return httpClient.get<BreakdownResponse>(`${BillsService.BASE}/${billId}/breakdown${qs}`);
  }

  /**
   * Every transaction behind one bill: the statement period's charges on the
   * card it settles, by category, with payments and credits apart and a
   * reconciliation to the statement. Read-only.
   */
  static async transactions(billId: string): Promise<BillTransactionsResponse> {
    return httpClient.get<BillTransactionsResponse>(`${BillsService.BASE}/${billId}/transactions`);
  }

  /** What is coming up in a period, priced from history. Read-only. */
  static async forecast(period?: string): Promise<ForecastResponse> {
    const qs = period ? `?period=${encodeURIComponent(period)}` : '';
    return httpClient.get<ForecastResponse>(`${BillsService.BASE}/forecast${qs}`);
  }

  /**
   * Settle a bill by repeating the previous payment's account and rail.
   *
   * The amount still comes from this period's statement when there is one;
   * `amount_basis` in the response says which was used, so a copied amount is
   * never mistaken for a billed one.
   */
  static async quickPay(
    billId: string, input: QuickPayInput = {},
  ): Promise<{ bill: Bill; entry_id: string; recorded_only: boolean; amount_basis?: string }> {
    return httpClient.post(`${BillsService.BASE}/${billId}/quick-pay`, input);
  }

  /** Auto-match manually-marked bills to real transactions. */
  /**
   * Run the scheduled-payment verifier for this household now. A dry run
   * reports what it would do and changes nothing.
   */
  static async runScheduled(dryRun = false): Promise<ScheduleRunResult> {
    return httpClient.post<ScheduleRunResult>(
      `${BillsService.BASE}/scheduled/run${dryRun ? '?dry_run=true' : ''}`, {});
  }

  static async reconcile(): Promise<{ matched: number }> {
    return httpClient.post(`${BillsService.BASE}/reconcile`, {});
  }

  static async saveBill(b: SaveBillInput): Promise<Bill> {
    return httpClient.post<Bill>(`${BillsService.BASE}`, b);
  }

  static async deleteBill(id: string): Promise<void> {
    await httpClient.delete(`${BillsService.BASE}/${id}`);
  }
}

export const billsService = BillsService;

/** Colour token per status, so the table reads at a glance. */
export const statusVar = (s: BillStatus): string => {
  switch (s) {
    case 'Paid': return '--inflow-text';
    case 'Overdue': return '--danger-text';
    case 'Due': return '--warn-text';
    case 'Upcoming': return '--text-dim';
    default: return '--text-faint';
  }
};
